import { useNavigate, useParams } from 'react-router-dom';

import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import WilayaDropdown from '@/components/WilayaDropdown';
import CommuneDropdown from '@/components/CommuneDropdown';
import { Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Spinner from '@/components/Spinner';
import { useUpdateClient } from '@/hooks/clients/useUpdateClient';
import Container from '@/components/Container';
import { useEffect, useState } from 'react';

const UpdateClient: React.FC = () => {
  const [showInfo, setShowInfo] = useState(false);
  const { id } = useParams(); // Get client ID from URL params
  const navigate = useNavigate();

  const { defaultValues, loading, error, updateClient } = useUpdateClient(
    id || ''
  );

  const form = useForm({
    defaultValues, // Set default values in the form
    mode: 'onChange',
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form]);

  const handleSubmit = async (values: any) => {
    try {
      await updateClient(values);
      navigate('/clients');
      toast.success('Client updated successfully!');
    } catch (error) {
      toast.error('Failed to update client.');
    }
  };

  if (loading) {
    return (
      <Container>
        <div className='h-screen flex items-center justify-center'>
          <Spinner />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className='h-screen flex items-center justify-center'>
          <p className='text-red-500'>{error}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container isSmallScreenReady>
      <div className='p-6 h-screen mt-12 md:mt-0'>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='flex flex-col gap-4 h-full '
          >
            {/* contact info  */}
            <h1 className='font-semibold  text-[18px]'>Update Client</h1>
            <div className='md:px-8 space-y-4 '>
              {/* Name and Phone */}
              <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                <FormField
                  control={form.control}
                  name='fullname'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input placeholder='Name' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className='flex gap-4 md:w-1/2'>
                  <div>
                    <div className='bg-white h-[44px] text-muted-foreground text-sm flex justify-center items-center gap-1 border border-muted-foreground rounded-md px-4'>
                      <img src='/assets/images/algeria.svg' alt='' />
                      (+213)
                    </div>
                  </div>
                  <FormField
                    control={form.control}
                    name='phone'
                    render={({ field }) => (
                      <FormItem className='w-[80%]'>
                        <FormControl>
                          <Input
                            type='number'
                            placeholder='07 77777777'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Company and Email */}
              <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                <FormField
                  control={form.control}
                  name='company'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input placeholder='Company' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input type='email' placeholder='Email' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Wilaya and Commune */}
              <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                <FormField
                  control={form.control}
                  name='wilaya'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <WilayaDropdown
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='commune'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <CommuneDropdown
                          wilaya={form.getValues('wilaya')}
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          placeholder='Commune'
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* position */}
              <div className='flex flex-col md:flex-row md:w-1/2 md:pr-4'>
                <div className='w-full'>
                  <FormField
                    control={form.control}
                    name='positionGps'
                    rules={{
                      pattern: {
                        value: /^-?\d{1,3}\.\d+,\s?-?\d{1,3}\.\d+$/,
                        message: 'Invalid GPS coordinates',
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className='relative'>
                            <Input
                              type='text'
                              placeholder='34.80786765474, 65.86557475346'
                              {...field}
                            />
                            <Info
                              className='absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-muted-foreground'
                              onClick={() => setShowInfo(!showInfo)}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                        {showInfo && (
                          <p className='text-gray-500 text-xs mt-2'>
                            To get coordinates, open Google Maps, right-click on
                            the location, and select the first option. Copy the
                            coordinates from the info box that appears.
                          </p>
                        )}
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <h1 className='font-semibold mt-8 text-[18px]'>
              Company information
            </h1>
            <div className='md:px-8 space-y-4'>
              {/* Trade and Fiscal IDs */}
              <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                <FormField
                  control={form.control}
                  name='tradeRegisterNumber'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input
                          type='number'
                          placeholder='Trade register number'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='fiscalId'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input
                          type='number'
                          placeholder='Fiscal identification number'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Statistical and Imposition IDs */}
              <div className='flex flex-col md:flex-row gap-4 md:gap-8'>
                <FormField
                  control={form.control}
                  name='statisticalId'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input
                          type='number'
                          placeholder='Statistical identification number'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='impositionArticleNumber'
                  render={({ field }) => (
                    <FormItem className='md:w-1/2'>
                      <FormControl>
                        <Input
                          type='number'
                          placeholder='Imposition article number'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className='flex flex-col md:flex-row gap-2 py-8 md:p-0 md:mt-auto mx-auto md:space-x-4'>
              <Button disabled={form.formState.isSubmitting} type='submit'>
                {form.formState.isSubmitting ? <Spinner /> : 'Update Client'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </Container>
  );
};

export default UpdateClient;
